/**
 * 店舗別ブログ作成分析 — what was produced, by whom, and which way it is moving.
 *
 * The screen answers three questions in the order an operator asks them:
 *
 *   HOW MUCH, this period and against the last one - the tiles.
 *   WHICH WAY - the chart, one measure at a time.
 *   WHO - the ranked list, where the row that stands out is the one to open.
 *
 * Two things decide the form rather than taste:
 *
 *   ONE MEASURE AT A TIME. Articles, images and spend have unrelated scales, and
 *   a second y-axis is the chart mistake that makes a reader see a crossing that
 *   is not there. The measure is a control, not a second line.
 *
 *   EVERY CLINIC AND EVERY STORE IS ON THE CHART. Colour is what does not scale
 *   past four series, so the tail is drawn in the de-emphasis grey rather than
 *   folded into an 「その他」 line that answers nothing about the store inside
 *   it. The crosshair names each series and its value for the month under the
 *   pointer, which is how the grey lines stay readable.
 *
 * And the title is now true. A period carries one row per SCOPE - the store's
 * row holds the articles and images it produced, the clinic's row holds the
 * money - and this screen used to sum both per clinic, throwing away the one
 * dimension its name promises. 店舗別 is a control now, and spend stays clinic
 * level because that is the scope it is recorded at.
 */
import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import type { AdminUsage, UsageResponse } from '../api.ts';
import {
  LineChart,
  SeriesTable,
  Sparkline,
  downloadCsv,
  type Series,
} from '../components/LineChart.tsx';
import { Card, Checklist, Empty, RowLink, Stat, Table, jaNumber } from '../components/ui.tsx';
import { InfoButton } from '../components/InfoButton.tsx';
import { Select } from '../components/Select.tsx';

const MONTH_OPTIONS = [3, 6, 12] as const;

/** The three measures, and what each is counted in. */
const MEASURES = {
  articles: { label: '記事作成数', unit: '件', axis: '件' },
  images: { label: '画像生成数', unit: '枚', axis: '枚' },
  cost: { label: 'AI利用額', unit: '', axis: 'USD' },
} as const;

type MeasureId = keyof typeof MEASURES;
type GroupId = 'store' | 'clinic';

/** YYYY-MM in the clinic's calendar; the API returns UTC ISO strings. */
const monthKey = (iso: string) => new Date(iso).toISOString().slice(0, 7);

/** Micro-USD to dollars, which is the only unit anybody reads spend in. */
const dollars = (micro: number) => Math.round((micro / 1_000_000) * 100) / 100;

/**
 * The change against the previous month.
 *
 * The arrow carries the direction, never colour alone - and the ink stays text
 * ink because down is not automatically bad here. Fewer articles in a month is a
 * fact about the month.
 */
function Delta({ current, previous, unit }: { current: number; previous: number; unit: string }) {
  if (previous === 0) {
    return <span className="delta">{current === 0 ? '前月と同じ' : '前月の実績なし'}</span>;
  }
  const change = current - previous;
  const share = Math.round((change / previous) * 100);
  if (change === 0) return <span className="delta">前月と同じ</span>;
  return (
    <span className="delta">
      <span className="delta__arrow" aria-hidden>
        {change > 0 ? '▲' : '▼'}
      </span>{' '}
      {change > 0 ? '+' : ''}
      {jaNumber(Math.round(change * 100) / 100)}
      {unit}（{share > 0 ? '+' : ''}
      {share}%）
    </span>
  );
}

export default function AdminAnalytics() {
  const { items, entities } = useLoaderData<UsageResponse>();
  const [months, setMonths] = useState<number>(6);
  const [clinicFilter, setClinicFilter] = useState<string>('');
  const [measure, setMeasure] = useState<MeasureId>('articles');
  const [group, setGroup] = useState<GroupId>('store');

  const clinics = useMemo(() => {
    // From the roster, not the ledger: a clinic that used nothing still exists.
    const byId = new Map<string, string>();
    for (const row of entities) byId.set(row.clinic_id, row.name);
    for (const row of items) byId.set(row.clinic_id, row.name);
    return [...byId].map(([id, name]) => ({ id, name }));
  }, [items, entities]);

  /*
   * Spend is recorded on the clinic's row, so there is no store to break it
   * down by. Said in the control rather than answered with an empty chart.
   */
  const grouping: GroupId = measure === 'cost' ? 'clinic' : group;

  const model = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - (months - 1));
    const from = cutoff.toISOString().slice(0, 7);

    const rows = items.filter(
      (row) =>
        monthKey(row.period_start) >= from &&
        (!clinicFilter || row.clinic_id === clinicFilter) &&
        // A row only counts for the measure its scope is responsible for.
        (measure === 'cost' ? row.store_id === null : row.store_id !== null),
    );

    /*
     * The months on the axis come from the whole ledger, not from the filtered
     * rows: filtering to a clinic that used nothing would otherwise leave no
     * months, and the screen would say "no data" about a clinic whose answer is
     * zero. Those are different statements.
     */
    const monthLabels = [
      ...new Set(
        items
          .filter(
            (row) =>
              monthKey(row.period_start) >= from &&
              (measure === 'cost' ? row.store_id === null : row.store_id !== null),
          )
          .map((row) => monthKey(row.period_start)),
      ),
    ].sort();

    const valueOf = (row: AdminUsage) =>
      measure === 'articles'
        ? Number(row.articles_used)
        : measure === 'images'
          ? Number(row.images_used)
          : dollars(Number(row.ai_micro_cost_used));

    /*
     * Grouped by whichever scope is being asked about. The key carries the id
     * so colour follows the entity - filtering the list never repaints the
     * series that remain.
     */
    const grouped = new Map<
      string,
      {
        label: string;
        clinicId: string;
        clinicName: string;
        storeId: string | null;
        byMonth: Map<string, number>;
      }
    >();
    for (const row of rows) {
      const id = grouping === 'store' ? (row.store_id ?? row.clinic_id) : row.clinic_id;
      const label =
        grouping === 'store' ? (row.store_name ?? `${row.name}（本部）`) : row.name;
      const entry = grouped.get(id) ?? {
        label,
        clinicId: row.clinic_id,
        clinicName: row.name,
        storeId: row.store_id,
        byMonth: new Map<string, number>(),
      };
      const key = monthKey(row.period_start);
      entry.byMonth.set(key, (entry.byMonth.get(key) ?? 0) + valueOf(row));
      grouped.set(id, entry);
    }

    /*
     * Everyone who exists, at zero if that is what they did.
     *
     * A ledger only records activity, so a screen built from it alone hides the
     * clinic that was sold a plan and wrote nothing - which is the row an
     * operator is usually looking for. Seeded after the real rows so a genuine
     * figure is never overwritten by a zero.
     */
    for (const entity of entities) {
      if (clinicFilter && entity.clinic_id !== clinicFilter) continue;
      if (grouping === 'store' && entity.store_id === null) continue;
      const id = grouping === 'store' ? entity.store_id! : entity.clinic_id;
      if (grouped.has(id)) continue;
      grouped.set(id, {
        label: grouping === 'store' ? (entity.store_name ?? '—') : entity.name,
        clinicId: entity.clinic_id,
        clinicName: entity.name,
        storeId: entity.store_id,
        byMonth: new Map<string, number>(),
      });
    }

    /*
     * Store names repeat across clinics - three 大阪店 in this installation - so
     * a bare name is not an identity. The clinic is added only to the ones that
     * collide: qualifying every label would make the legend unreadable to solve
     * a problem most rows do not have.
     */
    const seen = new Map<string, number>();
    for (const [, entry] of grouped) {
      seen.set(entry.label, (seen.get(entry.label) ?? 0) + 1);
    }

    const built = [...grouped]
      .map(([id, entry]) => ({
        id,
        label:
          grouping === 'store' && (seen.get(entry.label) ?? 0) > 1
            ? `${entry.label}（${entry.clinicName}）`
            : entry.label,
        clinicId: entry.clinicId,
        clinicName: entry.clinicName,
        storeId: entry.storeId,
        values: monthLabels.map((label) => entry.byMonth.get(label) ?? 0),
      }))
      .map((entry) => ({
        ...entry,
        total: entry.values.reduce((sum, value) => sum + value, 0),
      }))
      .sort((a, b) => b.total - a.total);

    /*
     * Every clinic and every store, as its own line.
     *
     * The tail used to fold into one 「その他」 series, which is the textbook
     * answer to "more series than the palette has hues" and the wrong one here:
     * this screen exists to see the whole estate, and a summed line answers
     * nothing about the store inside it. The chart draws them all and greys
     * everything past the fourth - colour is what does not scale, not the data -
     * and the crosshair names each one with its value.
     */
    const series: Series[] = built.map((entry) => ({
      id: entry.id,
      label: entry.label,
      values: entry.values,
    }));

    const byMonthTotal = monthLabels.map((_, index) =>
      built.reduce((sum, entry) => sum + (entry.values[index] ?? 0), 0),
    );
    const total = byMonthTotal.reduce((sum, value) => sum + value, 0);

    return {
      labels: monthLabels,
      series,
      rows: built,
      total,
      current: byMonthTotal.at(-1) ?? 0,
      previous: byMonthTotal.at(-2) ?? 0,
    };
  }, [items, entities, months, clinicFilter, measure, grouping]);

  const spec = MEASURES[measure];
  const hasData = model.labels.length > 0 && model.rows.length > 0;
  const round = (value: number) => Math.round(value * 100) / 100;
  /** One place decides how a measure is written, so every figure agrees. */
  const format = (value: number) =>
    measure === 'cost' ? `$${round(value).toFixed(2)}` : `${jaNumber(round(value))} ${spec.unit}`;

  return (
    <>
      <Card
        title="店舗別ブログ作成分析"
        caption="請求期間の実績です。記事と画像は店舗の実績、AI利用額はクリニック単位で記録されます。"
        actions={
          <div className="chips">
            <InfoButton title="この画面の読み方">
              <p>
                クリニックの<strong>利用実績台帳</strong>を集計しています。記事を書いた時点ではなく、
                その記事が属する<strong>請求期間</strong>に計上されます。
              </p>

              <h3 className="infobody__title">数え方</h3>
              <Checklist
                items={[
                  '記事数・画像数は店舗の実績として記録される（どの店舗の記事かが決まっているため）',
                  'AI利用額はクリニック単位で記録される（プランの枠がクリニック単位のため）',
                  '請求期間はクリニックごとに異なる。暦月ではなく契約の期間で区切られる',
                  '予約中（生成の途中）の分は含まない。確定した実績のみ',
                ]}
              />

              <h3 className="infobody__title">グラフについて</h3>
              {/*
                Describes what the chart DOES. It used to describe two things it
                does not: a bar rendering below three months (there is no bar
                path in LineChart.tsx, and nothing branches on the month count),
                and folding the tail into an 「その他」 series (every series is
                drawn - see the note at the top of LineChart.tsx, which argues
                against exactly that: a merged line answers nothing, so the tail
                is drawn in the de-emphasis grey and read back through the
                crosshair). 「ほか N 件」 in the tooltip is a count of the
                remaining series, not a series of its own.
              */}
              <p className="small">
                指標は1つずつ表示します。記事数とAI利用額は桁が違うため、
                同じ軸に重ねると読み違えるからです。
                系列は上位4件まで色分けし、5件目からはグレーで描きます
                （見分けのつかない色を増やすより、まとめて隠さないほうが読めるためです）。
                月にカーソルを合わせると、その月の全系列を多い順に表示します。
                色の判別に頼らずに読めるよう、下の表に同じ数値を出しています。
              </p>
            </InfoButton>
            <button
              className="btn btn--sm"
              type="button"
              disabled={!hasData}
              onClick={() =>
                downloadCsv(
                  `analytics-${measure}-${grouping}-${months}m.csv`,
                  model.labels,
                  model.series,
                )
              }
            >
              CSV出力
            </button>
          </div>
        }
      >
        {/*
          The headline figures, before the chart. What an operator wants first is
          "how much, and is that more than last month" - which is a number and a
          delta, not a shape.
        */}
        <div className="grid grid--4">
          <Stat
            label={`${spec.label}・期間合計`}
            value={format(model.total)}
            small
          />
          <Stat
            label="直近月"
            value={format(model.current)}
            hint={
              <Delta
                current={model.current}
                previous={model.previous}
                unit={measure === 'cost' ? '' : spec.unit}
              />
            }
            small
          />
          <Stat
            label="月平均"
            value={format(model.total / Math.max(model.labels.length, 1))}
            small
          />
          <Stat
            label={grouping === 'store' ? '対象店舗' : '対象クリニック'}
            value={jaNumber(model.rows.length)}
            hint={<span className="delta">{jaNumber(model.labels.length)}ヶ月分</span>}
            small
          />
        </div>

        {/* Filters in one row above what they filter. */}
        <div className="filters">
          <div className="field" style={{ marginBottom: 0, minWidth: 180 }}>
            <label htmlFor="measure-filter">指標</label>
            <Select
              id="measure-filter"
              value={measure}
              onChange={(next) => setMeasure(next as MeasureId)}
              options={Object.entries(MEASURES).map(([value, one]) => ({
                value,
                label: one.label,
              }))}
            />
          </div>
          <div className="field" style={{ marginBottom: 0, minWidth: 160 }}>
            <label htmlFor="group-filter">集計単位</label>
            <Select
              id="group-filter"
              value={grouping}
              onChange={(next) => setGroup(next as GroupId)}
              options={[
                { value: 'store', label: '店舗別' },
                { value: 'clinic', label: 'クリニック別' },
              ]}
            />
            {measure === 'cost' && (
              <span className="field__hint">
                AI利用額はクリニック単位の記録のため、店舗別には分けられません。
              </span>
            )}
          </div>
          <div className="field" style={{ marginBottom: 0, minWidth: 220 }}>
            <label htmlFor="clinic-filter">クリニック</label>
            <Select
              id="clinic-filter"
              value={clinicFilter}
              onChange={setClinicFilter}
              options={[
                { value: '', label: 'すべてのクリニック' },
                ...clinics.map((clinic) => ({ value: clinic.id, label: clinic.name })),
              ]}
            />
          </div>
          <div className="field" style={{ marginBottom: 0, minWidth: 140 }}>
            <label htmlFor="range-filter">期間</label>
            <Select
              id="range-filter"
              value={String(months)}
              onChange={(next) => setMonths(Number(next))}
              options={MONTH_OPTIONS.map((option) => ({
                value: String(option),
                label: `直近${option}ヶ月`,
              }))}
            />
          </div>
        </div>

        {!hasData ? (
          <Empty>
            対象期間の実績がありません。記事や画像が生成されると、その請求期間に計上されます。
          </Empty>
        ) : (
          <LineChart
            labels={model.labels}
            series={model.series}
            unit={spec.unit}
            /*
             * Money is written as money on the axis and in the crosshair too -
             * a chart reading 0.1 beside a tile reading $0.10 makes the reader
             * do the conversion.
             */
            {...(measure === 'cost' ? { format: (value: number) => `$${value.toFixed(2)}` } : {})}
          />
        )}
      </Card>

      {hasData && (
        <Card
          title={grouping === 'store' ? '店舗別の実績' : 'クリニック別の実績'}
          caption="多い順。行を選ぶとその詳細画面を開きます。"
        >
          <Table
            head={
              <>
                <th scope="col">#</th>
                <th scope="col">{grouping === 'store' ? '店舗' : 'クリニック'}</th>
                <th scope="col">推移</th>
                <th scope="col" className="num">
                  期間合計
                </th>
                <th scope="col">構成比</th>
                <th scope="col" className="num">
                  直近月
                </th>
              </>
            }
          >
            {model.rows.map((row, rank) => {
              const share = model.total === 0 ? 0 : (row.total / model.total) * 100;
              return (
                <tr key={row.id} className="row-link">
                  <td className="num muted">{rank + 1}</td>
                  <td>
                    <RowLink
                      to={
                        row.storeId
                          ? `/admin/stores/${row.storeId}`
                          : `/admin/clinics/${row.clinicId}`
                      }
                    >
                      {row.storeId ? (row.label.split('（')[0] ?? row.label) : row.label}
                    </RowLink>
                    {/* Which clinic it belongs to, always - store names repeat. */}
                    {row.storeId && (
                      <span className="muted small" style={{ display: 'block' }}>
                        {row.clinicName}
                      </span>
                    )}
                  </td>
                  <td>
                    <Sparkline values={row.values} label={`${row.label}の推移`} />
                  </td>
                  <td className="num">{format(row.total)}</td>
                  <td>
                    {/*
                      Share of the period, as a bar and as a number. One hue: this
                      is a magnitude of one measure, not an identity.
                    */}
                    <div className="sharecell">
                      <span className="barcompare__track" style={{ inlineSize: 84 }}>
                        <span
                          className="barcompare__fill"
                          style={{ inlineSize: `${Math.max(share, share > 0 ? 2 : 0)}%` }}
                        />
                      </span>
                      <span className="muted small">{share.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="num">
                    {format(row.values.at(-1) ?? 0)}
                    <span style={{ display: 'block' }}>
                      <Delta
                        current={row.values.at(-1) ?? 0}
                        previous={row.values.at(-2) ?? 0}
                        unit={measure === 'cost' ? '' : spec.unit}
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
          </Table>
        </Card>
      )}

      {hasData && (
        <Card
          title="データ表"
          caption="グラフと同じ数値です。色の判別に頼らず読めるようにしています。"
        >
          <SeriesTable
            labels={model.labels}
            series={model.series}
            rowHeader={grouping === 'store' ? '店舗' : 'クリニック'}
          />
        </Card>
      )}
    </>
  );
}
